import { type JSX } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { FolderVersion } from 'Models/Folders';
import detailsPageStyles from 'Styles/DetailsPage.module.css';

import styles from './FolderVersionSnapshot.module.css';

export interface FolderVersionSnapshotProps {
    documentApi: DocumentApi;
    version: FolderVersion;
}

/**
 * Renders the details of a `FolderVersion`.
 * Used in the "main" area of the Folder Details page, and in historical modals on the same page.
 */
export const FolderVersionSnapshot = (props: FolderVersionSnapshotProps): JSX.Element => {
    const { version } = props;
    const cachedData = useCachedData();

    /** Returns a given string as-is, or '-' if the value is empty or undefined. */
    const getDisplayValue = (value?: string): string => {
        return value && value.trim() ? value : '-';
    };

    /** Returns a displayable date for a given ISO 8601 timestamp, or '-' if the timestamp is undefined. */
    const getDisplayDate = (timestamp?: string): string => {
        return timestamp ? iso8601ToUsDateShort(timestamp) : '-';
    };

    const files = version.files ?? [];

    return (
        <div className={detailsPageStyles.detailsContainer}>
            <div className={detailsPageStyles.detailsGridSection}>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Folder Type" />
                        <Text>{getDisplayValue(version.type)}</Text>
                    </div>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Effective Date" />
                        <Text>{getDisplayDate(version.effective_date)}</Text>
                    </div>
                </div>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Last Updated By" />
                        <Text>{getUserNameFromSubject(version.last_updated_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? '-'}</Text>
                    </div>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Expiration Date" />
                        <Text>{getDisplayDate(version.expiration_date)}</Text>
                    </div>
                </div>
                <div className={detailsPageStyles.detailsGridColumn}>
                    <div className={detailsPageStyles.labelAndValueContainer}>
                        <FieldLabel fieldName="Last Updated Date" />
                        <Text>{getDisplayDate(version.last_updated)}</Text>
                    </div>
                </div>
            </div>
            <div className={detailsPageStyles.labelAndValueContainer}>
                <FieldLabel fieldName="Comments" />
                <Text preserveLineBreaks>{getDisplayValue(version.comments)}</Text>
            </div>
            <div className={detailsPageStyles.labelAndValueContainer}>
                <FieldLabel fieldName="Documents" />
                {files.length === 0 ? (
                    <Text>-</Text>
                ) : (
                    <div className={styles.documents}>
                        {[...files]
                            .sort((a, b) => (a.filename > b.filename ? 1 : -1))
                            .map((file) => (
                                <div key={file.file_id} className={styles.uploadedFile}>
                                    <UploadedFileAndState file={file} documentApi={props.documentApi} onDownloadError={console.log} />
                                </div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
};
